/* eslint-disable @angular-eslint/directive-class-suffix */
/* eslint-disable @angular-eslint/no-host-metadata-property */
/* eslint-disable @typescript-eslint/no-explicit-any */

import { AfterViewInit, Directive, ElementRef, inject, Injector, OnDestroy } from '@angular/core';
import { ControlValueAccessor, NgControl, ValidationErrors } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';

@Directive({
    standalone: false
})
export abstract class ValueAccessor implements ControlValueAccessor, AfterViewInit, OnDestroy {
  protected lastValue: any = null;
  protected _element = inject(
    ElementRef<
      HTMLElement & {
        invalid?: boolean;
        validationMessage?: string;
        customValidationMessages?: Record<string, string>;
      }
    >,
  );

  protected _ngControl!: NgControl;

  private injector = inject(Injector);
  private _destroy$ = new Subject<void>();

  public ngAfterViewInit() {
    this._ngControl = this.injector.get(NgControl);

    if (this._ngControl && this._ngControl.statusChanges) {
      this._ngControl.statusChanges.pipe(takeUntil(this._destroy$)).subscribe(() => {
        this.updateValidationState();
      });
    }
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public handleBlurEvent(): void {
    const control = this._ngControl?.control;
    if (!control) return;

    const wasUntouched = !control.touched;
    this._onTouched();

    if (wasUntouched && control.invalid) {
      // Focusing on an empty required field
      // and then blurring it immediately
      // will trigger a validation error.
      control.updateValueAndValidity();
    }
  }

  public writeValue(value: any): void {
    if (this.lastValue !== value) {
      this._element.nativeElement.value = this.lastValue = value == null ? '' : value;
    }
  }

  public handleChangeEvent(value: any): void {
    if (value !== this.lastValue) {
      this.lastValue = value;
      this._onChange(value);
    }
  }

  public registerOnChange(fn: (value: any) => void): void {
    this._onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    const element = this._element.nativeElement as HTMLElement & { disabled?: boolean };
    element.disabled = isDisabled;
  }

  protected updateValidationState(): void {
    if (!this._ngControl || !this._ngControl.control) return;

    const control = this._ngControl.control;
    const element = this._element.nativeElement;
    const invalid = control.invalid && control.touched;

    // Set invalid state
    element.invalid = invalid;

    if (invalid && control.errors) {
      // Check if there's a custom validation message already set
      const customMessage = element.hasAttribute('validation-message')
        ? element.getAttribute('validation-message')
        : null;

      // Use custom message if available, otherwise generate one
      if (customMessage) {
        element.validationMessage = customMessage;
      } else {
        element.validationMessage = this.getValidationMessage(control.errors);
      }
    } else {
      element.validationMessage = '';
    }
  }

  protected getValidationMessage(errors: ValidationErrors): string {
    if (!errors) return '';

    const element = this._element.nativeElement;

    // Check component's customValidationMessages property first
    if (element.customValidationMessages) {
      for (const errorType in errors) {
        if (Object.prototype.hasOwnProperty.call(errors, errorType)) {
          if (element.customValidationMessages[errorType]) {
            return element.customValidationMessages[errorType];
          }
        }
      }
    }

    // Check for error-specific custom messages in attributes
    for (const errorType in errors) {
      if (Object.prototype.hasOwnProperty.call(errors, errorType)) {
        const customAttr = `validation-message-${errorType}`;
        if (element.hasAttribute(customAttr)) {
          const attrValue = element.getAttribute(customAttr);
          if (attrValue) return attrValue;
        }
      }
    }

    const fieldName = this._element.nativeElement.getAttribute('label') || 'Dit veld';
    if (errors['required']) {
      return `${fieldName} is verplicht.`;
    } else if (errors['pattern']) {
      return `Ongeldig formaat voor ${fieldName.toLowerCase()}.`;
    } else if (errors['email']) {
      return 'Ongeldig e-mailadres. Gebruik een formaat zoals naam@voorbeeld.com.';
    } else if (errors['min']) {
      return `Waarde moet minimaal ${errors['min'].min} zijn.`;
    } else if (errors['max']) {
      return `Waarde mag maximaal ${errors['max'].max} zijn.`;
    } else if (errors['minlength']) {
      const requiredLength = errors['minlength'].requiredLength;
      return `${fieldName} moet minstens ${requiredLength} ${requiredLength === 1 ? 'teken' : 'tekens'} bevatten.`;
    } else if (errors['maxlength']) {
      const requiredLength = errors['maxlength'].requiredLength;
      return `${fieldName} mag maximaal ${requiredLength} ${requiredLength === 1 ? 'teken' : 'tekens'} bevatten.`;
    }

    // For other errors, use the first error key
    // This part might also be flagged by some linters if 'errors' could have prototype properties,
    // but Object.keys already returns only own enumerable properties.
    const errorKeys = Object.keys(errors);
    if (errorKeys.length > 0) {
      const errorKey = errorKeys[0];
      return errors[errorKey]?.message || `Ongeldige waarde (${errorKey})`;
    }

    return 'Ongeldige waarde'; // Fallback if no specific message and no error keys
  }

  protected _onChange: (value: any) => void = () => {
    /**/
  };

  protected _onTouched: () => void = () => {
    /**/
  };
}
